import React, { useState, useEffect } from 'react';
import { Book, BookStatus } from '../../types/Book';
import { useBooks } from '../../hooks/useBooks';
import BookList from './BookList';
import FavouriteBooks from './FavouriteBooks';
import BookForm from './BookForm';
import OnlineBookReader from './OnlineBookReader';
import PageHeader from '../common/PageHeader';
import './Library.css';

interface LibraryProps {
  user: any;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type LibraryTab = 'all' | 'favorites';

const Library: React.FC<LibraryProps> = ({ 
  user, 
  onClose,
  onToggleSidebar 
}) => {
  const [activeTab, setActiveTab] = useState<LibraryTab>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | undefined>();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isReadingOnline, setIsReadingOnline] = useState(false);
  const [selectedOnlineBook, setSelectedOnlineBook] = useState<Book | null>(null);

  // Load books only when this component is mounted
  const { 
    books, 
    loading, 
    error, 
    addBook, 
    updateBook, 
    deleteBook,
    searchBooksWithFilters,
    refreshBooks 
  } = useBooks(user);

  const handleAddBook = () => {
    setEditingBook(undefined);
    setShowForm(true);
  };

  const handleEditBook = (book: Book) => {
    setEditingBook(book);
    setShowForm(true);
  };

  const handleSubmitBook = async (book: Book) => {
    if (editingBook) {
      // Update existing book
      const success = await updateBook(book);
      if (success) {
        setShowForm(false);
        setEditingBook(undefined);
      }
    } else {
      // Add new book
      const { id, ...bookData } = book;
      const newBook = await addBook(bookData);
      if (newBook) {
        setShowForm(false);
        setEditingBook(undefined);
      }
    }
  };

  const handleReadOnline = (book: Book) => {
    setSelectedOnlineBook(book);
    setIsReadingOnline(true);
  };

  const handleStopOnlineReading = () => {
    setIsReadingOnline(false);
    setSelectedOnlineBook(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingBook(undefined);
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'all':
        return '📚 My Library';
      case 'favorites':
        return '❤️ Favourite Books';
      default:
        return '📚 Library';
    }
  };

  // Show online book reader when reading online
  if (isReadingOnline && selectedOnlineBook) {
    return (
      <div className="App fullscreen-reading">
        <OnlineBookReader 
          book={selectedOnlineBook}
          onClose={handleStopOnlineReading}
        />
      </div>
    );
  }

  return (
    <div className="library-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{getTabTitle()}</h1>}
        rightActions={activeTab === 'all' ? (
          <>
            <button 
              className="page-header-icon-btn"
              onClick={() => setIsFilterDrawerOpen(true)}
              title="Search & Filter"
            >
              🔍
            </button>
            <button className="page-header-icon-btn" onClick={handleAddBook} title="Add Book">
              +
            </button>
          </>
        ) : undefined}
      />

      <div className="library-tabs">
        <button 
          className={`library-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <span className="tab-icon">📚</span>
          <span className="tab-label">All Books</span>
        </button>
        <button 
          className={`library-tab ${activeTab === 'favorites' ? 'active' : ''}`}
          onClick={() => setActiveTab('favorites')}
        >
          <span className="tab-icon">❤️</span>
          <span className="tab-label">Favorites</span>
        </button>
      </div>

      <div className="library-content">
        {activeTab === 'all' ? (
          <BookList
            books={books}
            loading={loading}
            error={error}
            user={user}
            onEditBook={handleEditBook}
            onDeleteBook={deleteBook}
            onReadOnline={handleReadOnline}
            onStatusChange={(book, newStatus) => {
              // Update local state and refresh books
              refreshBooks();
            }}
            onSearchWithFilters={searchBooksWithFilters}
            selectedSubject={null}
            onClearSubject={() => {}}
            isFilterDrawerOpen={isFilterDrawerOpen}
            onFilterDrawerToggle={setIsFilterDrawerOpen}
          />
        ) : (
          <FavouriteBooks 
            onStartReading={(book) => {
              if (book) {
                handleReadOnline(book);
              }
            }}
            onStopReading={() => {}}
          />
        )}
      </div>

      {showForm && (
        <BookForm
          book={editingBook}
          onSubmit={handleSubmitBook}
          onCancel={handleCancelForm}
        />
      )}
    </div>
  );
};

export default Library;
